<script setup lang="ts">
import {ref} from 'vue';
import axios from 'axios';
import {
  ArrowRight,
  BriefcaseMedical,
  Check,
  CheckCircle2,
  Clock3,
  FileText,
  HeartHandshake,
  Send,
  Stethoscope,
  Upload,
  X
} from 'lucide-vue-next';
import '@/assets/styles/career.css';

const assistantPosition = 'Assistenz der Geschäftsführung und Pflegeleitung (m/w/d)';

const form = ref({
  name: '',
  forename: '',
  email: '',
  number: '',
  position: assistantPosition,
  resume: null as File | null,
  cover: null as File | null,
  data: false
});

const showForm = ref(false);
const sending = ref(false);
const submitted = ref(false);
const error = ref('');

const handleFileUpload = (event: Event, type: 'resume' | 'cover') => {
  const target = event.target as HTMLInputElement;
  form.value[type] = target.files?.[0] ?? null;
};

const openForm = () => {
  showForm.value = true;
  submitted.value = false;
  error.value = '';
};

const closeForm = () => {
  showForm.value = false;
  submitted.value = false;
  error.value = '';
};

const resetForm = () => {
  form.value = {
    name: '',
    forename: '',
    email: '',
    number: '',
    position: assistantPosition,
    resume: null,
    cover: null,
    data: false
  };
};

const submitForm = async () => {
  sending.value = true;
  error.value = '';

  try {
    const formData = new FormData();
    formData.append('name', form.value.name);
    formData.append('forename', form.value.forename);
    formData.append('email', form.value.email);
    formData.append('number', form.value.number);
    formData.append('position', form.value.position);

    if (form.value.resume) formData.append('resume', form.value.resume);
    if (form.value.cover) formData.append('cover', form.value.cover);

    await axios.post('/api/application', formData);
    submitted.value = true;
    resetForm();
  } catch {
    error.value = 'Die Bewerbung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.';
  } finally {
    sending.value = false;
  }
};
</script>

<template>
  <main class="career-page">
    <section class="career-hero">
      <div class="career-container career-hero-content">
        <span class="career-eyebrow">Karriere beim Gallus Pflegedienst</span>
        <h1>Gemeinsam für Menschen.<br><em>Mit Herz und Verantwortung.</em></h1>
        <p>
          Bei uns verbinden sich fachliche Qualität, menschliche Nähe und ein starkes Miteinander.
          Entdecken Sie Ihre beruflichen Möglichkeiten beim Gallus Pflegedienst.
        </p>
        <div class="career-tags">
          <span><Check/> Ambulante Pflege</span>
          <span><Check/> Wertschätzendes Team</span>
          <span><Check/> Frankfurt am Main</span>
        </div>
      </div>
    </section>

    <section class="career-section benefits-section">
      <div class="career-container">
        <div class="career-section-heading">
          <div>
            <span class="career-eyebrow">Das erwartet Sie</span>
            <h2>Ein Arbeitsplatz,<br>an dem Sie zählen.</h2>
          </div>
          <p>
            Wir arbeiten engagiert für die Menschen, die uns vertrauen. Dabei setzen wir auf
            Fachkompetenz, gegenseitige Unterstützung und eine offene Kommunikation.
          </p>
        </div>

        <div class="career-benefits">
          <article>
            <HeartHandshake/>
            <h3>Persönliches Miteinander</h3>
            <p>Kurze Wege, direkte Abstimmung und ein wertschätzender Umgang im Team.</p>
          </article>
          <article>
            <BriefcaseMedical/>
            <h3>Sinnvolle Arbeit</h3>
            <p>Mit Ihrer Arbeit leisten Sie jeden Tag einen wichtigen Beitrag für andere Menschen.</p>
          </article>
          <article>
            <Stethoscope/>
            <h3>Professionelles Umfeld</h3>
            <p>Klare Abläufe und fachlicher Austausch bilden die Grundlage unserer Arbeit.</p>
          </article>
        </div>
      </div>
    </section>

    <section class="career-section jobs-section">
      <div class="career-container">
        <div class="jobs-heading">
          <span class="career-eyebrow">Offene Stelle</span>
          <h2>Vielleicht passen wir zusammen.</h2>
          <p>Für den ersten Schritt reichen wenige Angaben. Ihre Unterlagen können Sie direkt hochladen.</p>
        </div>

        <article class="job-list-card">
          <div class="job-main">
            <span class="job-status">Aktuell gesucht</span>
            <h3>Assistenz der Geschäftsführung und Pflegeleitung <small>(m/w/d)</small></h3>
            <p>
              Wir suchen in Teilzeit oder geringfügiger Beschäftigung eine zuverlässige,
              strukturierte Persönlichkeit, die unsere Geschäftsführung und Pflegeleitung
              organisatorisch unterstützt. Die Arbeitszeiten und Arbeitstage werden flexibel
              miteinander abgestimmt.
            </p>
            <ul>
              <li><Check/> Pflegerischer oder medizinischer Hintergrund</li>
              <li><Check/> Strukturierte und zuverlässige Arbeitsweise</li>
              <li><Check/> Organisationstalent und freundliche Kommunikation</li>
            </ul>
          </div>
          <div class="job-action">
            <div class="job-pills">
              <span><Clock3/> Teilzeit oder geringfügig</span>
              <span><Clock3/> 9–12 Std./Woche</span>
              <span><Check/> Variable Tage nach Absprache</span>
              <span><Stethoscope/> Pflege &amp; Medizin</span>
            </div>
            <button class="career-button" type="button" @click="openForm">
              Jetzt bewerben <ArrowRight/>
            </button>
          </div>
        </article>
      </div>
    </section>

    <transition name="career-modal">
      <div v-if="showForm" class="application-overlay" @click.self="closeForm">
        <div class="application-modal" role="dialog" aria-modal="true" aria-labelledby="application-title">
          <button class="modal-close" type="button" aria-label="Bewerbungsformular schließen" @click="closeForm">
            <X/>
          </button>

          <div v-if="submitted" class="application-success">
            <CheckCircle2/>
            <h2>Vielen Dank!</h2>
            <p>Ihre Bewerbung wurde erfolgreich übermittelt. Wir melden uns persönlich bei Ihnen.</p>
            <button class="career-button" type="button" @click="closeForm">Schließen</button>
          </div>

          <template v-else>
            <div class="modal-heading">
              <span class="career-eyebrow">Direkt bewerben</span>
              <h2 id="application-title">Wir freuen uns auf Sie.</h2>
              <p>{{ assistantPosition }}</p>
            </div>

            <form class="application-form" @submit.prevent="submitForm">
              <div class="field-row">
                <label>
                  <span>Vorname *</span>
                  <input v-model="form.forename" required autocomplete="given-name" placeholder="Max">
                </label>
                <label>
                  <span>Nachname *</span>
                  <input v-model="form.name" required autocomplete="family-name" placeholder="Mustermann">
                </label>
              </div>

              <div class="field-row">
                <label>
                  <span>E-Mail *</span>
                  <input v-model="form.email" required type="email" autocomplete="email" placeholder="mail@beispiel.de">
                </label>
                <label>
                  <span>Telefon *</span>
                  <input v-model="form.number" required type="tel" autocomplete="tel" placeholder="+49 …">
                </label>
              </div>

              <div class="upload-grid">
                <label class="upload-card">
                  <input type="file" accept="application/pdf,.pdf" @change="handleFileUpload($event, 'resume')">
                  <Upload/>
                  <span>
                    <strong>Lebenslauf hochladen</strong>
                    <small>{{ form.resume ? form.resume.name : 'PDF auswählen (optional)' }}</small>
                  </span>
                </label>
                <label class="upload-card">
                  <input type="file" accept="application/pdf,.pdf" @change="handleFileUpload($event, 'cover')">
                  <FileText/>
                  <span>
                    <strong>Anschreiben hochladen</strong>
                    <small>{{ form.cover ? form.cover.name : 'PDF auswählen (optional)' }}</small>
                  </span>
                </label>
              </div>

              <label class="privacy-check">
                <input v-model="form.data" required type="checkbox">
                <span>
                  Ich stimme der Verarbeitung meiner Bewerbungsdaten gemäß
                  <router-link to="/datenschutz" target="_blank">Datenschutzerklärung</router-link> zu.
                </span>
              </label>

              <p v-if="error" class="form-error">{{ error }}</p>
              <button class="career-button submit-application" :disabled="sending">
                <Send/>
                {{ sending ? 'Wird übermittelt …' : 'Bewerbung senden' }}
              </button>
            </form>
          </template>
        </div>
      </div>
    </transition>
  </main>
</template>
